<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useRoute } from "vue-router";
import { api, useSession, errorText } from "../api";
import McpPermissions from "../components/McpPermissions.vue";
import "../styles/mcp.scss";
const route = useRoute(),
  session = useSession(),
  loading = ref(true),
  busy = ref(false),
  error = ref("");
const request = String(route.query.request ?? "");
const client = ref<{ name: string; scopes: string[]; redirectUri: string; redirectOrigin:string; verified:boolean }>();
const scopes = ref<string[]>([]),
  datasetIds = ref<string[] | null>(null),
  datasets = ref<{ id: string; name: string }[]>([]),
  allowed = ref<string[]>([]);
const loadedOrganization = ref("");
let loadVersion = 0;
const valid = computed(
  () =>
    !loading.value &&
    loadedOrganization.value === session.organization &&
    scopes.value.length > 0 &&
    (datasetIds.value === null || datasetIds.value.length > 0),
);
async function load() {
  const version = ++loadVersion,
    organization = session.organization;
  loading.value = true;
  error.value = "";
  loadedOrganization.value = "";
  client.value = undefined;
  scopes.value = [];
  allowed.value = [];
  datasets.value = [];
  if (datasetIds.value !== null) datasetIds.value = [];
  try {
    const [info, data, settings] = await Promise.all([
      api("/api/mcp/authorize/" + encodeURIComponent(request)),
      api("/api/datasets?status=all"),
      api("/api/mcp"),
    ]);
    if (version !== loadVersion || organization !== session.organization)
      return;
    client.value = info;
    datasets.value = data;
    allowed.value = info.scopes.filter(
      (s: string) => s !== "data:manage" || settings.canManage,
    );
    scopes.value = [...allowed.value];
    loadedOrganization.value = organization;
  } catch (e) {
    if (version === loadVersion) error.value = errorText(e);
  } finally {
    if (version === loadVersion) loading.value = false;
  }
}
onMounted(load);
async function consent(approve: boolean) {
  if (approve && !valid.value) return;
  busy.value = true;
  error.value = "";
  try {
    const result = await api(
      "/api/mcp/authorize/" + encodeURIComponent(request),
      {
        method: "POST",
        body: {
          approve,
          scopes: scopes.value,
          datasetIds: approve ? datasetIds.value : null,
        },
      },
    );
    window.location.assign(result.redirect);
  } catch (e) {
    error.value = errorText(e);
    busy.value = false;
  }
}
</script>
<template>
  <section class="mcp-consent">
    <p v-if="loading" role="status">Henter forbindelsesforsøg…</p>
    <div v-if="error" role="alert" class="error">{{ error }}</div>
    <template v-if="client"
      ><h1 class="mcp-break">Forbind {{ client.verified ? client.name : client.redirectOrigin }} med Yawhub</h1>
      <p v-if="!client.verified" class="warning"><strong>Uverificeret klient.</strong> Klienten kalder sig “{{client.name}}”. Navnet er selvvalgt og er ikke verificeret af Yawhub. Kontrollér returadressen, før du giver adgang.</p>
      <p>
        Vælg, hvad denne klient må gøre på dine vegne. Du kan til enhver tid
        trække adgangen tilbage under AI & MCP.
      </p>
      <p class="muted small">
        Klientens returadresse:
        <span class="mcp-break">{{ client.redirectUri }}</span>
      </p>
      <label v-if="session.organizations.length > 1" class="mcp-label"
        >Workspace<select v-model="session.organization" @change="load">
          <option v-for="o in session.organizations" :key="o.id" :value="o.id">
            {{ o.name }}
          </option>
        </select></label
      ><McpPermissions
        v-model:scopes="scopes"
        v-model:dataset-ids="datasetIds"
        :allowed="allowed"
        :datasets="datasets"
      />
      <div class="mcp-actions">
        <button
          class="button primary"
          :disabled="busy || !valid || loading"
          @click="consent(true)"
        >
          {{ busy ? "Forbinder…" : "Godkend forbindelse" }}</button
        ><button
          class="button secondary"
          :disabled="busy"
          @click="consent(false)"
        >
          Afvis
        </button>
      </div></template
    ><RouterLink v-else-if="!loading" to="/connections" class="button secondary"
      >Tilbage til forbindelser</RouterLink
    >
  </section>
</template>
